import { AlertTriangle } from "lucide-react";
import { useNow } from "@/hooks/useNow";
import { cn } from "@/lib/utils";
import {
  describeIndexingProgress,
  DocumentIndexingView,
  INDEXING_EXPECTATION,
} from "@/pages/workspace/utils/indexingProgress";

// Minute-granular text: a 30 s tick keeps it at most half a minute behind.
const CLOCK_TICK_MS = 30_000;

const StalledNote = ({ text }: { text: string }) => (
  <span className="inline-flex items-center gap-1 text-foreground">
    <AlertTriangle
      className="size-3 shrink-0 text-warning"
      aria-hidden="true"
    />
    {text}
  </span>
);

/** The line under a file's name while kildeklargøring runs (LAR-486). */
export const IndexingProgressLine = ({
  view,
  className,
}: {
  view: DocumentIndexingView;
  className?: string;
}) => {
  const now = useNow(CLOCK_TICK_MS);
  const { line, stalled } = describeIndexingProgress(view, now);
  return (
    <span className={cn("text-xs text-muted-foreground", className)}>
      {/* First, so truncation in a narrow row or card never hides it. */}
      {stalled && (
        <>
          <StalledNote
            text={stalled.charAt(0).toUpperCase() + stalled.slice(1)}
          />
          {" · "}
        </>
      )}
      {line}
    </span>
  );
};

/** The spinner tooltip's content while kildeklargøring runs. */
export const IndexingProgressTooltip = ({
  view,
}: {
  view: DocumentIndexingView;
}) => {
  const now = useNow(CLOCK_TICK_MS);
  const { title, detail, stalled } = describeIndexingProgress(view, now);
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{title}</span>
      {detail && <span>{detail}</span>}
      {stalled ? (
        <span>
          Klargøringen {stalled}. Fortsætter den ikke, kan du genkøre den 30
          minutter efter start.
        </span>
      ) : (
        <span>{INDEXING_EXPECTATION}</span>
      )}
    </div>
  );
};
